"use client"

import { createContext, useContext, useState, useCallback, useRef } from "react"
import { createPortal } from "react-dom"
import { GlassToast } from "@/src/components/ui/glass-toast"
import { CheckCircle2, AlertCircle, Info } from "lucide-react"
import { Z } from "@/lib/z-index"

type ToastType = "success" | "error" | "info"

interface ToastItem {
  id: number
  message: string
  type: ToastType
  createdAt: number
}

interface ToastContextValue {
  success: (message: string) => void
  error: (message: string) => void
  info: (message: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error("useToast must be used within ToastProvider")
  return ctx
}

const ICON_MAP: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />,
  error: <AlertCircle className="h-4 w-4 text-red-500 shrink-0" />,
  info: <Info className="h-4 w-4 text-blue-500 shrink-0" />,
}

/** 同时显示的最大 toast 数量 */
const MAX_TOASTS = 3
/** 相同消息在此时间窗口内不重复弹出 (ms) */
const DEDUP_WINDOW = 500
/** 自动消失时间 (ms) */
const TOAST_DURATION = 3000

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const idRef = useRef(0)

  const addToast = useCallback((message: string, type: ToastType) => {
    const now = Date.now()
    setToasts((prev) => {
      // 去重：短时间内相同类型 + 相同消息不重复弹出
      const isDuplicate = prev.some(
        (t) => t.message === message && t.type === type && now - t.createdAt < DEDUP_WINDOW
      )
      if (isDuplicate) return prev

      const id = ++idRef.current
      const next = [...prev, { id, message, type, createdAt: now }]
      // 超出上限时移除最旧的
      return next.length > MAX_TOASTS ? next.slice(-MAX_TOASTS) : next
    })
  }, [])

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const value: ToastContextValue = {
    success: useCallback((msg: string) => addToast(msg, "success"), [addToast]),
    error: useCallback((msg: string) => addToast(msg, "error"), [addToast]),
    info: useCallback((msg: string) => addToast(msg, "info"), [addToast]),
  }

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== "undefined" && toasts.length > 0 &&
        createPortal(
          <div
            className="glass-toast-container"
            style={{
              position: "fixed",
              top: "1.5rem",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: Z.TOAST,
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
              pointerEvents: "none",
              width: "min(24rem, calc(100vw - 3rem))",
            }}
          >
            {toasts.map((toast) => (
              <GlassToast
                key={toast.id}
                open
                onOpenChange={(open) => { if (!open) removeToast(toast.id) }}
                duration={TOAST_DURATION}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {ICON_MAP[toast.type]}
                  <span className="text-xs font-medium min-w-0 break-all">{toast.message}</span>
                </div>
              </GlassToast>
            ))}
          </div>,
          document.body
        )
      }
    </ToastContext.Provider>
  )
}
